import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, ArrowUp, Lock, CheckCircle, Star } from 'lucide-react';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { useNavigate } from 'react-router-dom';
import { CareerAnalysisData, safeParseCareerAnalysis } from '../../types/analysis';

interface SkillsAnalysisProps {
  analysis: CareerAnalysisData | null;
}

const SkillsAnalysis: React.FC<SkillsAnalysisProps> = ({ analysis }) => {
  const { plan } = useSubscriptionStore();
  const navigate = useNavigate();
  const skills = analysis?.umiejetnosci || [];
  
  const isPremium = plan === 'premium';
  
  if (!skills.length) {
    return null;
  }

  // Dla użytkowników free pokazujemy tylko część umiejętności
  const visibleSkills = isPremium ? skills : skills.slice(0, 2);
  const hiddenSkills = isPremium ? [] : skills.slice(2);

  const getSkillIcon = (index: number) => {
    // Proste ikony bez logiki priorytetów
    const icons = [Star, CheckCircle, Lightbulb];
    return icons[index % icons.length]; // Cykliczne użycie ikon
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="relative overflow-hidden"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900 flex items-center">
          <Lightbulb className="w-5 h-5 mr-2 text-yellow-500" />
          Kluczowe umiejętności
        </h3>
      </div>

      <div className="space-y-4">
        {visibleSkills.map((skill, index) => {
          const IconComponent = getSkillIcon(index);
          return (
            <div
              key={index}
              className="p-4 border border-gray-200 rounded-xl bg-white hover:border-yellow-300 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-start gap-3">
                  <div className="bg-yellow-50 p-2 rounded-full mt-0.5">
                    <IconComponent className="h-5 w-5 text-yellow-500" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{skill.nazwa}</h4>
                    <p className="text-sm text-gray-600 mt-1">{skill.opis}</p>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
        
        {/* Zablurowane umiejętności dla użytkowników darmowych */}
        {!isPremium && hiddenSkills.length > 0 && (
          <div className="relative mt-6">
            {hiddenSkills.slice(0, 2).map((skill, index) => {
              const IconComponent = getSkillIcon(visibleSkills.length + index);
              return (
                <div
                  key={`hidden-${index}`}
                  className="p-4 border border-gray-200 rounded-xl mb-4 opacity-60 blur-[4px] bg-white select-none"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-start gap-3">
                      <div className="bg-yellow-50 p-2 rounded-full mt-0.5">
                        <IconComponent className="h-5 w-5 text-yellow-500" />
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{skill.nazwa}</h4>
                        <p className="text-sm text-gray-600 mt-1">{skill.opis}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="bg-white/90 p-4 rounded-xl shadow-sm text-center">
                <Lock className="w-5 h-5 text-yellow-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-gray-800 mb-2">
                  Odblokuj {hiddenSkills.length} więcej kluczowych umiejętności
                </p>
                <button
                  onClick={() => navigate('/plany')}
                  className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                >
                  Przejdź do Premium
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default SkillsAnalysis;
